import { Dialog, Transition } from "@headlessui/react";
import {
  CheckIcon,
  DownloadIcon,
  ExclamationIcon,
  XIcon
} from "@heroicons/react/solid";
import { find, isEmpty, last, map, toLower, values } from "lodash";
import Papa from "papaparse";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { DOMParser } from "xmldom";
import FileUpload from "../../../components/FileUpload";
import LoadingIcon from "../../../components/icons/LoadingIcon";
import { getColorFromId } from "../../../components/labels/utils";
import { useBillingPlans } from "../../../context/billingPlans";
import { useLocations } from "../../../context/locations";
import { LocationValues } from "../../../context/locations/LocationsProvider";
import { useWorkspaces } from "../../../context/workspaces";
import { downloadCsv } from "../../../utils/csv";
import readFileAsText from "../../../utils/readFileAsText";
import { PaymentStep } from "../../../utils/types";
import useTransmitScaleConfig from "../../../utils/useTransmitScaleConfig";

interface LocationCsvRow {
  name: string;
  address: string;
  lat: string;
  lng: string;
  height: number;
  elevation?: number;
  notes?: string;
  schema_error?: string;
}

const kmlParse = require("../../../utils/kml-parse"); // TODO: lazy-load

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  setErrorMessage: (message: string | null) => void;
}

const ImportLocationsModal: React.FC<Props> = ({
  open,
  setOpen,
  setErrorMessage
}) => {
  const { currentWorkspace } = useWorkspaces();
  const { locations, addLocations, locationsLimitExceeded } = useLocations();
  const { currentPlan, setPaymentStep } = useBillingPlans();
  const setDisabledIds = useTransmitScaleConfig(
    (state) => state.setDisabledIds
  );
  const [files, setFiles] = useState<File[]>([]);
  const [uploadWarnings, setUploadWarnings] = useState<JSX.Element[]>([]);
  const [isImporting, setIsImporting] = useState(false);

  const coordinatesExistAlready = useCallback(
    (lat: number, lng: number) =>
      !!find(
        values(locations),
        (location) => location.lat === lat && location.lng === lng
      ),
    [locations]
  );

  const importLocations = useCallback(async () => {
    if (isEmpty(files)) return;

    setIsImporting(true);
    setUploadWarnings([]);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const extension = toLower(last(file.name.split(".")));

      const name = file.name.replace(`.${extension}`, "");

      let newLocations: LocationValues[] = [];
      let invalidLocations: any[] = [];

      switch (extension) {
        case "kml": {
          const fileString = await readFileAsText(file);

          const kmlDom = new DOMParser().parseFromString(fileString);

          kmlParse
            .parse(kmlDom)
            .geoJSON.features.forEach((f: any, index: number) => {
              let location: LocationValues = {
                name: "",
                lat: 0,
                lng: 0,
                address: null,
                height: 0
              };

              if (
                f?.geometry?.type !== "Point" ||
                f?.type !== "Feature" ||
                !f.properties?.name ||
                !f.geometry?.coordinates
              ) {
                return;
              }

              location = {
                name: f.properties.name,
                lat: f.geometry.coordinates[1],
                lng: f.geometry.coordinates[0],
                address: null,
                height: f.properties.altitude || 0
              };

              if (
                coordinatesExistAlready(
                  Number(location.lat.toFixed(5)),
                  Number(location.lng.toFixed(5))
                )
              ) {
                invalidLocations.push({
                  ...location,
                  schema_error: "Duplicate coordinates"
                });

                return;
              }

              newLocations.push(location);
            });
          break;
        }
        case "csv": {
          Papa.parse<LocationCsvRow>(file, {
            delimitersToGuess: [";", ","],
            header: true,
            trimHeaders: true,
            skipEmptyLines: true,
            complete: (results) => {
              results.data.forEach((result) => {
                const { name, lat, lng, address, height, notes, elevation } =
                  result;

                let location: LocationValues = {
                  name: "",
                  lat: 0,
                  lng: 0,
                  address: null,
                  height: 0
                };

                if (!name || !lat || !lng) return;

                location = {
                  name,
                  lat: parseFloat(lat.replace(",", ".")),
                  lng: parseFloat(lng.replace(",", ".")),
                  address: (address ?? notes?.replaceAll('"', "")) || null,
                  height: Number(height || elevation || 0)
                };

                if (
                  coordinatesExistAlready(
                    Number(location.lat.toFixed(5)),
                    Number(location.lng.toFixed(5))
                  )
                ) {
                  invalidLocations.push({
                    ...location,
                    schema_error: "Duplicate coordinates"
                  });

                  return;
                }

                newLocations.push(location);
              });
            }
          });

          break;
        }
      }

      setTimeout(async () => {
        const upToXlocations =
          currentWorkspace?.active_plan?.allowed_locations ?? 0;

        if (locationsLimitExceeded && !isEmpty(newLocations)) {
          setUploadWarnings((prevState) => [
            ...prevState,
            <span>
              This workspace has the {currentPlan?.name} plan which is limited
              to {upToXlocations} locations. You are trying to add a new
              location resulting in {upToXlocations + newLocations.length}{" "}
              locations, which is not allowed. Please{" "}
              <NavLink
                className="font-bold dark:text-hotspotty-300 hover:underline"
                onClick={() => {
                  setPaymentStep(PaymentStep.plans);
                }}
                to={"/pricing"}
              >
                increase your limits
              </NavLink>{" "}
              (every paid hotspot and simulation comes with two locations) or
              remove locations you no longer need.
            </span>
          ]);

          setIsImporting(false);

          return;
        }

        if (newLocations.length > MAXIMUM_LOCATIONS_PER_FILE) {
          setUploadWarnings((prevState) => [
            ...prevState,
            <>
              The import is limited to {MAXIMUM_LOCATIONS_PER_FILE} entries,
              this has {newLocations.length}
            </>
          ]);

          setIsImporting(false);

          return;
        }

        if (!isEmpty(newLocations)) {
          await addLocations(newLocations, [
            { name, color: getColorFromId(name) }
          ]);
          setDisabledIds(map(newLocations, "id"));
        }

        if (!isEmpty(invalidLocations)) {
          setErrorMessage(
            "Some of the rows failed to be imported. The downloaded csv file contains the invalid lines along with an error message."
          );

          const locationsCsvContent = Papa.unparse(invalidLocations, {
            delimiter: ";"
          });

          const a = document.createElement("a");

          let downloadLink = window.URL.createObjectURL(
            new Blob([locationsCsvContent], {
              type: "text/csv"
            })
          );

          a.href = downloadLink;
          a.download = `${name}_invalid_rows.csv`;
          a.click();
        }

        setIsImporting(false); // TODO: this should happen after the last file finished processing
        setOpen(false);
      }, 100);
    }
  }, [
    files,
    coordinatesExistAlready,
    currentWorkspace?.active_plan?.allowed_locations,
    locationsLimitExceeded,
    setOpen,
    currentPlan?.name,
    setPaymentStep,
    addLocations,
    setDisabledIds,
    setErrorMessage
  ]);

  const uploadWarnHandler = useCallback(
    (errs) => {
      setUploadWarnings(errs);
    },
    [setUploadWarnings]
  );

  const uploadChangeHandler = useCallback((files) => {
    setFiles(files);
  }, []);

  useEffect(() => {
    if (!open) return;

    setFiles([]);
    setUploadWarnings([]);
  }, [open]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-10 inset-0 overflow-y-auto"
        open={open}
        onClose={setOpen}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400"
                  onClick={() => setOpen(false)}
                >
                  <span className="sr-only">Close</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="mt-3 sm:mt-0">
                <Dialog.Title
                  as="h3"
                  className="text-lg leading-6 font-medium text-gray-900 dark:text-white"
                >
                  Import locations
                </Dialog.Title>

                <p className="mt-4 dark:text-gray-300">
                  Upload a <i>*.kml</i> or <i>*.csv</i> file to import
                  locations.
                </p>

                <ul className="my-4 space-y-4">
                  <li className="flex space-x-3">
                    <CheckIcon
                      className="flex-shrink-0 h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      The <i>*.csv</i> file needs to be{" "}
                      <b>delimited by semicolon (;) or comma (,)</b>
                    </span>
                  </li>
                  <li className="flex space-x-3">
                    <CheckIcon
                      className="flex-shrink-0 h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      The first line needs to have exactly the following
                      (case-sensitive) columns:
                      <strong className="ml-1 break-all">
                        name;lat;lng;address;height
                      </strong>
                    </span>
                  </li>
                  <li className="flex space-x-3">
                    <CheckIcon
                      className="flex-shrink-0 h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      The <i>name</i>, <i>lat</i> and <i>lng</i> fields are
                      required
                    </span>
                  </li>
                  <li className="flex space-x-3">
                    <CheckIcon
                      className="flex-shrink-0 h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      The <i>height</i> field represent the installation height
                      above the ground in meters
                    </span>
                  </li>
                  <li className="flex space-x-3">
                    <CheckIcon
                      className="flex-shrink-0 h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      Locations for which the coordinates already exists are
                      ignored when importing
                    </span>
                  </li>
                </ul>

                <div className="my-10">
                  <FileUpload
                    onChange={uploadChangeHandler}
                    onError={uploadWarnHandler}
                    limit={3}
                    limitSize={10}
                    extensions={["kml", "csv"]}
                  />
                </div>

                {!isEmpty(uploadWarnings) && (
                  <div className="mt-1">
                    <div className="rounded-md bg-yellow-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <ExclamationIcon
                            className="h-5 w-5 text-yellow-400"
                            aria-hidden="true"
                          />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">
                            Warning! Some files were not uploaded.
                          </h3>
                          <div className="mt-2 text-sm text-yellow-700">
                            {uploadWarnings.map((warn, index) => (
                              <p key={index}>{warn}</p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-5 sm:mt-4 sm:flex justify-between">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 sm:mt-0 sm:w-auto sm:text-sm dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
                  onClick={() => {
                    downloadCsv(
                      [
                        {
                          name: "Middle of nowhere",
                          lat: 38.774449,
                          lng: -8.747053,
                          address: "",
                          height: 10
                        },
                        {
                          name: "Commercial building",
                          lat: 38.723404,
                          lng: -9.147853,
                          address:
                            "Avenida Da Liberdade 203, 1250-148 Lisbon, Lisboa, Portugal",
                          height: 30
                        }
                      ],
                      "hotspotty-locations-example"
                    );
                  }}
                >
                  <span>Download example file</span>
                  <DownloadIcon className="ml-2 h-4 w-4" aria-hidden="true" />
                </button>

                <div>
                  <button
                    type="button"
                    className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 sm:text-sm dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
                    onClick={() => setOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="mt-3 sm:mt-0 sm:ml-4 w-full sm:w-auto inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 items-center disabled:opacity-50 disabled:cursor-default"
                    onClick={importLocations}
                    disabled={isEmpty(files) || isImporting}
                  >
                    {isImporting && (
                      <LoadingIcon className="w-4 h-4 animate-spin -ml-1 mr-3" />
                    )}
                    <span>{isImporting ? "Importing" : "Import"}</span>
                  </button>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ImportLocationsModal;

//
// Utils
//

const MAXIMUM_LOCATIONS_PER_FILE = 2000;
